// ---------------------------------------------------------------------------
// Cloudflare Custom Hostnames SSL Vocabulary — DOM-010 Phase 1
// (constants + reason enum + state-transition guard)
// ---------------------------------------------------------------------------
// Story DOM-010: "SSL is automatically provisioned/renewed and visible for
// Cloudflare-managed custom domains."
//
// Per docs/vision_execution/openclaw/DOM-010-DECISION-PACKET.md, the gap is
// not a missing column — `customDomains.ssl_status` already exists in
// `packages/db/src/schema/page-builder-domains.ts:75`. The gaps are:
//
//   (1) the existing `SSL_STATUSES = ["pending","active","expired","failed"]`
//       vocabulary is too coarse to model the CF Custom Hostnames state
//       machine, which distinguishes `pending_validation` (DCV in progress)
//       from `pending_issuance` (DCV passed, cert being issued). The
//       packet's "Minimal contract" §Schema row mandates the closed set
//       ["pending_validation","pending_issuance","active","expired",
//       "failed"].
//   (2) no closed failure-reason vocabulary — the packet's "Failure carries
//       a reason code (`validation_timed_out`, `dcv_error`, `internal_error`)"
//       row demands an enum the future poller writes to
//       `custom_domains.ssl_status_reason`.
//   (3) no canonical CQRS event-type constant for
//       `domain.ssl_state_changed@v1` — the future `domain-verifier`
//       worker (couples with DOM-018) emits this on every transition per
//       the packet's "CQRS event" section.
//
// Phase 1 scope (this file): pure constant vocabulary + state-transition
// guard. The CF Custom Hostnames API client (new
// `packages/dns-scanner/src/cloudflare-custom-hostnames.ts`), the polling
// step (co-located with DOM-018 verifier worker), the `provisionSsl`
// wire-up in `workers/api-pages/src/deps/domain-queries.ts`, the schema
// migration adding `ssl_status_updated_at` + `ssl_status_reason` +
// `cf_custom_hostname_id` + the broadened `chk_custom_domains_ssl_status`
// CHECK constraint, and the `domain.ssl_state_changed@v1` event emission
// all land in later phases governed by the decision packet.
//
// Why a separate file from `page-builder-domains.ts`: the legacy
// `SSL_STATUSES` is still consumed by existing reads + the current CHECK
// constraint, so we cannot rename it in Phase 1 without breaking the
// column. The new vocabulary lives side-by-side until the Phase 2
// migration renames the canonical set; both vocabularies converge then.
// ---------------------------------------------------------------------------

/**
 * Closed status vocabulary for the CF Custom Hostnames SSL state machine.
 * The future verifier writes one of these to `customDomains.ssl_status`
 * once the Phase 2 migration broadens the column CHECK constraint.
 *
 *   - pending_validation: CF awaiting Domain Control Validation (DCV)
 *   - pending_issuance:   DCV passed, certificate being issued
 *   - active:             certificate live, auto-renewing
 *   - expired:            legacy state; CF auto-renews so this should be rare
 *   - failed:             validation_timed_out, dcv_error, or internal_error
 *                         (reason stored in `ssl_status_reason`)
 *
 * Order pins the UI badge dropdown enumeration + the future column CHECK
 * constraint. Append-only — additions are fine, removals require a
 * registry update + decision-packet revision.
 */
export const CF_CUSTOM_HOSTNAME_SSL_STATUSES = [
  "pending_validation",
  "pending_issuance",
  "active",
  "expired",
  "failed",
] as const;

export type CfCustomHostnameSslStatus =
  (typeof CF_CUSTOM_HOSTNAME_SSL_STATUSES)[number];

/**
 * Closed failure-reason vocabulary written to
 * `customDomains.ssl_status_reason` (new column landing in Phase 2)
 * whenever `ssl_status === "failed"`. Per the DOM-010 packet's "Failure
 * carries a reason code" invariant.
 *
 * The reason string is part of the UI badge text + the
 * `domain.ssl_state_changed@v1` CQRS event payload. Auditors replay
 * reason strings verbatim — the order here pins the serializer contract.
 *
 *   - validation_timed_out: DCV TXT/HTTP token never appeared within CF's
 *     SLA — typically the tenant never published the DCV record or DNS
 *     never propagated. UI surfaces a retry CTA.
 *   - dcv_error: CF observed the DCV token but rejected it (token
 *     mismatch, encoding error, or hostname mismatch). UI surfaces a
 *     "verify DNS" CTA.
 *   - internal_error: CF API itself returned 5xx, or the platform's
 *     CF token lacks Custom Hostnames scope. Operator must remediate;
 *     the tenant cannot fix this themselves.
 */
export const CF_CUSTOM_HOSTNAME_SSL_FAILURE_REASONS = [
  "validation_timed_out",
  "dcv_error",
  "internal_error",
] as const;

export type CfCustomHostnameSslFailureReason =
  (typeof CF_CUSTOM_HOSTNAME_SSL_FAILURE_REASONS)[number];

/**
 * Canonical CQRS event type the future unified `domain-verifier` worker
 * emits on every SSL state transition. The `@v1` suffix follows the
 * `customer_events` catalog versioning rule so a v2 payload shape can land
 * later without breaking existing materializers.
 *
 * Couples with DOM-008's `email.ses_verification_state_changed@v1` and
 * DOM-018's `domain.verification_state_changed@v1` — three distinct event
 * types, never compound, never alias. The same worker emits all three but
 * the catalog keeps them separate so the projection layer can route per
 * surface (UI domain badge, email deliverability page, audit log).
 */
export const CF_CUSTOM_HOSTNAME_SSL_STATE_CHANGED_EVENT =
  "domain.ssl_state_changed@v1" as const;

export type CfCustomHostnameSslStateChangedEvent =
  typeof CF_CUSTOM_HOSTNAME_SSL_STATE_CHANGED_EVENT;

/**
 * Runtime narrowing guard for the status vocabulary. The verifier MUST
 * call this on any raw CF API response before writing the row — CF's
 * upstream `CustomHostname.ssl.status` enumeration includes
 * `initializing` and `pending_deployment` values the platform collapses
 * into `pending_validation` and `pending_issuance` respectively, so the
 * adapter must map and validate.
 */
export function isCfCustomHostnameSslStatus(
  value: unknown,
): value is CfCustomHostnameSslStatus {
  return (
    typeof value === "string" &&
    (CF_CUSTOM_HOSTNAME_SSL_STATUSES as readonly string[]).includes(value)
  );
}

/**
 * Runtime narrowing guard for the failure-reason vocabulary.
 */
export function isCfCustomHostnameSslFailureReason(
  value: unknown,
): value is CfCustomHostnameSslFailureReason {
  return (
    typeof value === "string" &&
    (CF_CUSTOM_HOSTNAME_SSL_FAILURE_REASONS as readonly string[]).includes(
      value,
    )
  );
}

/**
 * State-transition guard. Returns `true` if `next` is a legal successor of
 * `current`. The verifier MUST consult this before writing the row so a
 * stale poll cannot regress `active → pending_validation` (analogous to
 * the DOM-008 packet's "Idempotent re-poll" invariant: re-running the
 * same attempt does not regress a terminal state).
 *
 * Legal transitions:
 *   pending_validation → pending_issuance | active | failed | pending_validation
 *                        (CF may mark the certificate active immediately after
 *                        DCV succeeds; self-loop is idempotent re-poll)
 *   pending_issuance   → active | failed | pending_issuance
 *                        (idempotent re-poll while cert being issued)
 *   active             → active | expired
 *                        (CF auto-renews; expired only on catastrophic
 *                        renewal failure)
 *   expired            → pending_validation | active | failed | expired
 *                        (operator-triggered re-provision restarts the
 *                        state machine; CF can also auto-recover)
 *   failed             → pending_validation | failed
 *                        (operator fixes DNS / IAM, taps "retry";
 *                        failed → failed is legal for a second
 *                        unsuccessful poll cycle)
 */
export function isLegalCfCustomHostnameSslTransition(
  current: CfCustomHostnameSslStatus,
  next: CfCustomHostnameSslStatus,
): boolean {
  switch (current) {
    case "pending_validation":
      return (
        next === "pending_issuance" ||
        next === "active" ||
        next === "failed" ||
        next === "pending_validation"
      );
    case "pending_issuance":
      return (
        next === "active" ||
        next === "failed" ||
        next === "pending_issuance"
      );
    case "active":
      // CF auto-renews. The only legal regression is `expired` on
      // catastrophic renewal failure — never back to a pending_* state
      // without an explicit operator-triggered re-provision (which
      // writes `expired` first, then the new flow starts fresh).
      return next === "active" || next === "expired";
    case "expired":
      // Operator-triggered re-provision restarts the state machine.
      // CF can also auto-recover an expired hostname back to active if
      // the cert renews after the expiry window — both legal.
      return (
        next === "pending_validation" ||
        next === "active" ||
        next === "failed" ||
        next === "expired"
      );
    case "failed":
      return next === "pending_validation" || next === "failed";
  }
}
